<!--StorefrontUI SfSelect modification-->

<template>
  <div
    class="ex-select w-auto"
    :class="{
      'is-selected': value || placeholder,
      'is-required': required,
      'is-disabled': disabled,
      'is-invalid': !valid
    }"
  >
    <div class="ex-select__wrapper w-full">
      <label
        :for="name"
        class="ex-select__label"
      >
        <slot
          name="label"
          :label="label"
        >
          {{ label }}
        </slot>
      </label>
      <select
        :id="name"
        :name="name"
        :value="value"
        :disabled="disabled"
        class="ex-select__dropdown w-full border border-EXPONDO-grey-20 bg-EXPONDO-white text-EXPONDO-black"
        :required="required"
        @change="changeHandler"
      >
        <!-- empty option by default, may be used as placeholder -->
        <option
          v-if="placeholder"
          class="ex-select__placeholder ex-select__option"
          disabled
          :selected="!!placeholder"
          value
        >
          <slot
            name="placeholder"
            v-bind="{ placeholder }"
          />
          {{ placeholder }}
        </option>
        <slot>
          <SelectOption
            v-for="option in optionsArray"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </SelectOption>
        </slot>
      </select>
      <span class="ex-select__arrow iconify svg--carrot-down border-l" />
      <span
        v-if="required"
        class="required-star w-5 flex items-center justify-center absolute top-0 bottom-0
        left-0 cursor-default text-EXPONDO-red"
      >
        *
      </span>
    </div>
    <div
      v-if="required"
      class="ex-select__error-message min-h-[14px]"
    >
      <transition name="sf-fade">
        <!-- @slot Custom error message of form select -->
        <slot
          v-if="!valid"
          name="errorMessage"
        >
          <span> {{ $t(errorMessage) }} </span>
        </slot>
      </transition>
    </div>
  </div>
</template>
<script lang="ts">

import type selectOptionType from '~~/types/checkout/formData/selectOptionType';

export default defineComponent({
  name: 'CustomSelect',
  props: {
    /**
     * Select field label
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Required attribute
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * Validate value of form select
     */
    valid: {
      type: Boolean,
      default: true,
    },
    /**
     * Disabled status of form select
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Error message value of form select. It will be appeared if `valid` is `true`.
     */
    errorMessage: {
      type: String,
      default: 'This field is not correct.',
    },
    /**
     * Value selected.
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * Adds placeholder
     */
    placeholder: {
      type: String,
      default: '',
    },
    optionsArray: {
      type: Array as PropType<Array<selectOptionType>>,
      default: () => {

        return [];
      
      },
    },
    name: {
      type: String,
      default: '',
    },
  },
  emits: ['input'],
  methods: {
    changeHandler(event) {

      this.$emit('input', event.target.value);
    
    },
  },
});

</script>
<style lang="scss" scoped>
.ex-select {
  padding-bottom: 0;
  padding-top: 0;
  min-width: var(--select-width);

  .ex-select__wrapper {
    position: relative;

    .ex-select__dropdown {
      border-radius: var(--form-border-radius);
      height: var(--select-dropdown-height);
      font-size: var(--font-size--sm);
      line-height: var(--h4-line-height);
    }

    .ex-select__arrow {
      background-color: white;
      position: absolute;
      right: 1px;
      top: 1px;
      background-size: 10px 4px;
      pointer-events: none;
      background-repeat: no-repeat;
      background-position: center;
      width: var(--select-arrow-width);
      height: calc(var(--select-dropdown-height) - 2px); //2px - top and bottom border
      border-radius: 0 var(--form-border-radius) var(--form-border-radius) 0;
    }

    .ex-select__arrow::before {
      @apply text-EXPONDO-grey;

      top: 30%;
      left: 30%;
      position: absolute;
    }
  }

  .ex-select__label {
    @apply text-EXPONDO-grey-60;

    margin-left: var(--spacer-sm);
    font-family: var(--font-family--primary);
    font-size: var(--font-size--sm);
    line-height: var(--h4-line-height);
    position: absolute;
    top: 25%;
  }

  .ex-select__label::after {
    @apply text-EXPONDO-grey-60;
  }

  .ex-select__error-message {
    min-width: var(--form-border-radius);
  }
}

.ex-select.is-selected {
  .ex-select__wrapper {
    .ex-select__dropdown {
      padding: 0 calc(var(--spacer-sm) + var(--select-arrow-width)) 0 var(--spacer-sm);
    }

    .ex-select__label {
      top: 0;
      font-size: var(--form-label-font-size);
    }
  }
}

/* Declared after `.is-selected` so this `padding-left` longhand overrides its
   `padding` shorthand, which has the same specificity. */
.ex-select.is-required {
  .ex-select__wrapper {
    .ex-select__dropdown {
      padding-left: 20px;
    }

    .ex-select__label {
      margin-left: 20px;
    }
  }
}

.ex-select.is-disabled {
  .ex-select__wrapper {
    .ex-select__dropdown {
      @apply bg-EXPONDO-grey-5;
    }

    .ex-select__arrow {
      @apply bg-EXPONDO-grey-5;
    }
  }
}

</style>
